import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useLocation } from "react-router-dom";
import { useAxiosAuth } from "../hooks/useAxiosAuth";
import AuthContext from "./AuthProvider";
import { COMPANY_COMPLIANCE_URL } from "../common/constants";

export interface ICompliance {
  compliant: boolean;
  company_approved: boolean;
  licence_valid: boolean;
  restricted: boolean;
  licence_number: string | null;
  licence_expires_at: string | null;
}

// MED-725: what the app assumes before the first successful fetch, and after a
// fetch that failed with nothing earlier to fall back on. Locked, by decision.
export const UNKNOWN_COMPLIANCE: ICompliance = {
  compliant: false,
  company_approved: false,
  licence_valid: false,
  restricted: true,
  licence_number: null,
  licence_expires_at: null,
};

export interface IComplianceContext {
  compliance: ICompliance;
  complianceKnown: boolean;
  refetchCompliance: () => void;
}

const ComplianceContext = createContext<IComplianceContext>({
  compliance: UNKNOWN_COMPLIANCE,
  complianceKnown: false,
  refetchCompliance: () => {},
});

export interface IComplianceProviderProps {
  children: JSX.Element;
}

// AuthContext is read directly rather than through hooks/useContexts, which
// imports this module — going through it would make the two files circular.
export function ComplianceProvider({ children }: IComplianceProviderProps) {
  const { auth } = useContext<any>(AuthContext);
  const axiosAuth = useAxiosAuth();
  const { pathname } = useLocation();

  const [compliance, setCompliance] = useState<ICompliance>(UNKNOWN_COMPLIANCE);
  const [complianceKnown, setComplianceKnown] = useState(false);

  // The request interceptor slides the token and calls setAuth, so depending on
  // auth.token directly would make each fetch trigger the next one.
  const tokenRef = useRef<string>(auth?.token ?? "");
  useEffect(() => {
    tokenRef.current = auth?.token ?? "";
  }, [auth?.token]);

  const hasToken = Boolean(auth?.token);

  const fetchCompliance = useCallback(async () => {
    if (!tokenRef.current) return;

    try {
      const res = await axiosAuth.get(COMPANY_COMPLIANCE_URL);
      if (!res?.data?.success || !res?.data?.data) return;

      setCompliance(res.data.data as ICompliance);
      setComplianceKnown(true);
    } catch (err: any) {
      // A failed refresh never downgrades a state already held. With nothing
      // held, the locked initial value stands.
      console.error(
        "Compliance fetch failed:",
        err?.response?.data?.message || err?.message,
      );
    }
  }, [axiosAuth]);

  useEffect(() => {
    if (!hasToken) {
      setCompliance(UNKNOWN_COMPLIANCE);
      setComplianceKnown(false);
      return;
    }

    fetchCompliance();
  }, [pathname, hasToken, fetchCompliance]);

  return (
    <ComplianceContext.Provider
      value={{ compliance, complianceKnown, refetchCompliance: fetchCompliance }}
    >
      {children}
    </ComplianceContext.Provider>
  );
}

export default ComplianceContext;
